import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { ArrowLeft, Phone, Mail, MapPin, Save, CheckCircle2, Clock, Plus, FileText } from "lucide-react";
import { format, parseISO } from "date-fns";

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [c, j] = await Promise.all([
        api.get(`/customers/${id}`),
        api.get(`/jobs`, { params: { customer_id: id } }),
      ]);
      setCustomer(c.data);
      setForm(c.data);
      setJobs(j.data);
    } catch {
      navigate("/customers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [id]);

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        phone: form.phone || "",
        email: form.email || "",
        address: form.address || "",
        property_notes: form.property_notes || "",
        service_notes: form.service_notes || "",
      };
      const { data } = await api.put(`/customers/${id}`, payload);
      setCustomer(data);
      toast.success("Saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (loading || !customer || !form) return <div className="overline">Loading…</div>;

  const completed = jobs.filter((j) => j.status === "completed");
  const scheduled = jobs.filter((j) => j.status !== "completed");

  return (
    <div data-testid="customer-detail-page">
      <Link to="/customers" className="inline-flex items-center gap-2 text-sm text-[#8ba394] hover:text-[#eae6db] mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to customers
      </Link>

      <div className="mb-8 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline mb-2">Customer</div>
          <h1 className="font-display text-4xl font-black tracking-tight leading-none">{customer.name}</h1>
        </div>
        <Link
          to={`/invoices?customer=${customer.id}`}
          className="bm-btn bm-btn-primary"
          data-testid="generate-invoice-button"
        >
          <FileText className="w-4 h-4" /> Generate Invoice
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <form onSubmit={onSave} className="bm-card p-5 lg:col-span-2 space-y-3" data-testid="customer-edit-form">
          <div className="flex items-center justify-between mb-2">
            <h2 className="overline">Edit Customer Details</h2>
            <span className="text-xs text-[#8ba394]">Changes save on submit</span>
          </div>
          <div>
            <label className="overline block mb-2">Name</label>
            <input required className="bm-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="overline block mb-2">Phone</label>
              <input className="bm-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
            <div>
              <label className="overline block mb-2">Email</label>
              <input className="bm-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
          </div>
          <div>
            <label className="overline block mb-2">Address</label>
            <input className="bm-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
          </div>
          <div>
            <label className="overline block mb-2">Property Notes</label>
            <textarea rows={2} className="bm-input" value={form.property_notes} onChange={(e) => setForm({ ...form, property_notes: e.target.value })} />
          </div>
          <div>
            <label className="overline block mb-2">Service Notes</label>
            <textarea rows={3} className="bm-input" value={form.service_notes} onChange={(e) => setForm({ ...form, service_notes: e.target.value })} />
          </div>
          <button type="submit" disabled={saving} className="bm-btn bm-btn-primary" data-testid="customer-save-button">
            <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save Changes"}
          </button>
        </form>

        <div className="space-y-4">
          <div className="bm-card p-5">
            <h2 className="overline mb-3">Contact</h2>
            {customer.phone && (
              <a href={`tel:${customer.phone}`} className="flex items-center gap-2 text-sm mb-2 text-[#eae6db] hover:text-[#e56b42]">
                <Phone className="w-4 h-4 text-[#e56b42]" /> <span className="font-mono">{customer.phone}</span>
              </a>
            )}
            {customer.email && (
              <a href={`mailto:${customer.email}`} className="flex items-center gap-2 text-sm mb-2 text-[#eae6db] hover:text-[#e56b42]">
                <Mail className="w-4 h-4 text-[#e56b42]" /> <span className="truncate">{customer.email}</span>
              </a>
            )}
            {customer.address && (
              <div className="flex items-start gap-2 text-sm text-[#b0c0b6] mt-2">
                <MapPin className="w-4 h-4 text-[#e56b42] mt-0.5" /> <span>{customer.address}</span>
              </div>
            )}
          </div>

          <div className="bm-card p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="overline">Upcoming Jobs</h2>
              <Link to="/schedule" className="text-xs text-[#8ba394] hover:text-[#eae6db]">
                <Plus className="w-3.5 h-3.5 inline" /> New
              </Link>
            </div>
            {scheduled.length === 0 ? (
              <div className="text-xs text-[#8ba394]">None scheduled.</div>
            ) : (
              <div className="space-y-2">
                {scheduled.map((j) => (
                  <div key={j.id} className="flex items-center justify-between text-sm py-1.5 border-b border-[#233129] last:border-0">
                    <div className="flex items-center gap-2">
                      <Clock className="w-3.5 h-3.5 text-[#8ba394]" />
                      <span className="font-mono text-[#e56b42]">{format(parseISO(j.scheduled_date), "MMM d")}</span>
                      <span className="text-[#b0c0b6]">{j.service_type}</span>
                    </div>
                    {j.frequency !== "once" && <span className="bm-badge bm-badge-sent text-[9px]">{j.frequency}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bm-card p-5">
            <h2 className="overline mb-3">Completed Jobs · {completed.length}</h2>
            {completed.length === 0 ? (
              <div className="text-xs text-[#8ba394]">No completed jobs yet.</div>
            ) : (
              <div className="space-y-2 max-h-64 overflow-auto">
                {completed.slice(0, 10).map((j) => (
                  <div key={j.id} className="flex items-center justify-between text-sm py-1.5 border-b border-[#233129] last:border-0">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-3.5 h-3.5 text-[#2d6a4f]" />
                      <span className="font-mono text-[#b0c0b6]">{format(parseISO(j.scheduled_date), "MMM d")}</span>
                    </div>
                    <span className="text-xs text-[#8ba394]">{j.service_type}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
